// eslint-disable-next-line @typescript-eslint/no-unused-vars-experimental
import type JCE from '@chesscom/js-chess-engine';
import type { EventHandlers, Send } from 'components/move-list/state-machine/types';
import type { GameAPI } from 'chessboard/types';
import type { Store, StoreActions } from 'components/move-list/store/types';
import type { MoveListComponent } from 'components/move-list/config/types';
import { Actions } from 'components/move-list/state-machine/types';
import { cleanCommentString } from 'components/move-list/helpers/clean-comment-string';
import { scrollToNode } from 'components/move-list-shared/scroll-to-node';
import { tick } from 'svelte';
import type { Queue } from 'src/utils/queue/config/types';
import { getMoveNodeEl } from '../helpers/dom/get-move-node-el';
import type { VariationPopoverProps } from '../store/types';

/**
 * Create piece and document pointer-event handlers that trigger state machine actions.
 */
export default function createEventHandlers(
  send: Send,
  {
    game,
    data,
    actions,
    queue,
  }: { game: GameAPI; data: Store; actions: StoreActions; queue: Queue.API },
): EventHandlers {
  return {
    /**
     * Node left pointer-down handler
     */
    selectNode(ids, event): void {
      if (event) {
        event.stopPropagation();
      }

      if (game.getOptions().disableUserNavigation) {
        return;
      }

      if (data.isActive.get() === false) {
        return;
      }
      selectNode(ids);
    },

    /**
     * Set the client rect of the selected node
     */
    setSelectedNodeEl(nodeEl): void {
      actions.setSelectedNodeEl(nodeEl);
    },

    /**
     * Open variations popover on right arrow key
     */
    openVariationsPopover(): void {
      actions.setVariationPopover({
        isOpen: true,
      });
    },

    /**
     * Set selected popover variation
     */
    selectPopoverMove(selectedIndex: VariationPopoverProps['selectedIndex']): void {
      actions.selectPopoverMove(selectedIndex);
    },

    /**
     * Left arrow key or outside popover menu click
     */
    closeVariationsPopover(): void {
      actions.setVariationPopover({
        isOpen: false,
      });
    },

    /**
     * Node right pointer-down handler to open context menu
     * Also used to reposition the context menu if it's already open
     * Disables default mouse event behavior if an event is provided and the menu is enabled
     */
    openContextMenu(node?: MoveListComponent.NodeModel, mouseEvent?: MouseEvent): void {
      if (game.getOptions().disableUserNavigation) {
        return;
      }

      if (data.isActive.get() === false) {
        return;
      }

      if (!data.contextMenu.get().enabled) {
        return;
      }

      if (node) {
        selectNode(node.ids);
      }

      mouseEvent?.preventDefault();

      // selecting the node above will put tasks on the queue,
      // so we need to open the context menu after those tasks.
      queue.add(() => {
        actions.setContextMenu({
          isOpen: true,
        });
      });
      queue.syncFlush();
    },

    /**
     * Outside context menu left pointer-down handler
     */
    closeContextMenu(): void {
      actions.setContextMenu({
        isOpen: false,
      });
    },

    handleContextMenuAction(action, payload): void {
      const contextMenuState = data.contextMenu.get();
      const keys = data.selectedNodeKeys.get();
      const handler = contextMenuState.actions?.[action]?.handler;
      if (!keys || !handler) {
        return;
      }

      const node = game.getNodeByIds(keys.ids);
      if (!node) {
        return;
      }

      handler({ node, game }, payload);
      this.closeContextMenu();
    },

    updateComment(comment: MoveListComponent.Comment, text: string | undefined): void {
      const cleanComment = cleanCommentString(text);
      const lineNumber = comment.node?.ids.line ?? comment.lineNumber;

      if (comment.isBeforeComment && lineNumber !== undefined) {
        game.updateLineComment(cleanComment, lineNumber);
      } else if (comment.node) {
        game.updateNode(comment.node.ids, { comment: cleanComment });
      }
    },

    clickComment(ids: JCE.Ids): void {
      if (game.getOptions().disableUserNavigation) {
        return;
      }
      game.selectNode(ids.line, ids.move);
    },

    async handleSelectedNodeChange() {
      // wait for svelte to update dom first
      await tick();

      const selectedNodeKeys = data.selectedNodeKeys.get();
      const moveListEl = data.moveListEl.get();

      const previousNodeEl = data.selectedNodeEl.get();
      previousNodeEl?.classList.remove('selected');

      if (!selectedNodeKeys || !moveListEl) {
        data.selectedNodeEl.set(null);
        return;
      }

      const nodeWrapperEl = getMoveNodeEl(
        moveListEl,
        selectedNodeKeys.ids.line,
        selectedNodeKeys.ids.move,
      ) as HTMLElement | null;

      const nodeEl = nodeWrapperEl?.querySelector('.node-highlight-content') as
        | HTMLElement
        | undefined;

      if (!nodeEl || !nodeWrapperEl) {
        data.selectedNodeEl.set(null);
        return;
      }

      const finishedInitialLoad = data.finishedInitialLoad.get();
      scrollToMoveNode(nodeWrapperEl, { scrollToTop: !finishedInitialLoad });

      if (!finishedInitialLoad) {
        data.finishedInitialLoad.set(true);
      }

      nodeEl.classList.add('selected');
      data.selectedNodeEl.set(nodeEl);
    },

    scrollToMoveNode,
  };

  /**
   * Scrolls to a move node element in the move list
   */
  function scrollToMoveNode(
    nodeEl: HTMLElement | null,
    options: { scrollToTop?: boolean } = {},
  ): void {
    if (!nodeEl) {
      return;
    }

    scrollToNode({
      nodeEl,
      moveListEl: data.moveListEl.get(),
      containerEl: data.scrollContainer.get(),
      position: options.scrollToTop ? 'top' : 'nearest-edge',
      onlyIfOutside: !options.scrollToTop,
    });
  }

  /**
   * select a node in the move list and update necessary game state.
   */
  function selectNode(ids: JCE.Ids) {
    const highlightedNodeKeys = data.selectedNodeKeys.get();
    const selectedNode = game.getSelectedNode();

    const gameNodeIsAlreadySelected =
      selectedNode?.ids.line === ids.line && selectedNode?.ids.move === ids.move;

    if (highlightedNodeKeys === null && gameNodeIsAlreadySelected) {
      // here we just need to update the move list state to highlight the node, since the game node is already selected.
      send(Actions.SELECT_NODE, { node: selectedNode });
    } else {
      game.selectNode(ids.line, ids.move);
    }
  }
}
